import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ExcelService } from 'src/app/services/excel.service';
import { LotteryService, LotteryBeneficiaryRow } from 'src/app/services/lottery.service';
import { LotteryDrillDownContext } from '../lottery-dashboard/lottery-dashboard.component';

@Component({
  selector: 'app-lottery-beneficiary-list',
  templateUrl: './lottery-beneficiary-list.component.html',
  styleUrls: ['./lottery-beneficiary-list.component.scss']
})
export class LotteryBeneficiaryListComponent implements OnInit {

  @Input() drillData!: LotteryDrillDownContext;
  @Input() divisionId!: number;
  @Input() divisionName!: string;
  @Input() subDivisionId!: number;
  @Input() subDivisionName!: string;
  @Input() villageId!: number;
  @Input() villageName!: string;
  @Input() selectedVillageTotals: { totalTarget: number; applicationsReceived: number; benefittedCount: number; balanceTarget: number };
  @Output() beneficiaryClosed = new EventEmitter<boolean>();
  @Output() beneficiaryClosedToDivision = new EventEmitter<boolean>();
  @Output() toDashboardComp = new EventEmitter<boolean>();

  loading = true;

  allData: LotteryBeneficiaryRow[] = [];
  filteredData: LotteryBeneficiaryRow[] = [];
  paginatedData: LotteryBeneficiaryRow[] = [];

  pageSize = 10;
  totalItems = 0;
  totalTarget = 0;
  totalApplied = 0;
  totalAssigned = 0;
  totalBalanceTarget = 0;

  bookingDetailsView = false;
  selectedBookingId: number | null = null;

  private expandedIndices = new Set<number>();

  @ViewChild('paginator') paginator: MatPaginator;

  private readonly excelColumns: string[] = [
    'Booking Reference', 'Booking Date', 'Mobile Number',
    'Farmer Community', 'Farmer Type', 'Product Name', 'Current Status'
  ];

  constructor(
    private excelService: ExcelService,
    private lotteryService: LotteryService
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.lotteryService
      .getBeneficiaryList(
        this.drillData.financialYearId, this.drillData.departmentId, this.drillData.month, this.drillData.dbtGoId,
        this.drillData.dbtSchemeId, this.drillData.lotteryRunId, this.divisionId, this.subDivisionId, this.villageId, true)
      .subscribe((result: LotteryBeneficiaryRow[]) => {
        this.allData = result || [];
        this.filteredData = this.allData.slice();
        this.totalItems = this.filteredData.length;
        this.updateSummary();
        this.updatePaginatedData();
        this.loading = false;
      }, () => {
        this.allData = [];
        this.filteredData = [];
        this.totalItems = 0;
        this.updateSummary();
        this.updatePaginatedData();
        this.loading = false;
      });
  }

  private updatePaginatedData(): void {
    const start = this.paginator ? this.paginator.pageIndex * this.paginator.pageSize : 0;
    const size = this.paginator ? this.paginator.pageSize : this.pageSize;
    this.paginatedData = this.filteredData.slice(start, start + size);

    // More than one record on the page: keep every card closed by default.
    // Exactly one record: show it expanded straight away.
    this.expandedIndices.clear();
    if (this.paginatedData.length === 1) {
      this.expandedIndices.add(0);
    }
  }

  toggleExpand(index: number): void {
    if (this.expandedIndices.has(index)) {
      this.expandedIndices.delete(index);
    } else {
      this.expandedIndices.add(index);
    }
  }

  isExpanded(index: number): boolean {
    return this.expandedIndices.has(index);
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.updatePaginatedData();
  }

  private updateSummary(): void {
    this.totalTarget = this.selectedVillageTotals.totalTarget;
    this.totalApplied = this.selectedVillageTotals.applicationsReceived;
    this.totalAssigned = this.selectedVillageTotals.benefittedCount;
    this.totalBalanceTarget = this.selectedVillageTotals.balanceTarget;
  }

  applyFilter(event: any): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (!filterValue) {
      this.filteredData = this.allData.slice();
    } else {
      this.filteredData = this.allData.filter(row =>
        (row.bookingReferenceNo || '').toLowerCase().includes(filterValue) ||
        (row.mobileNumber || '').includes(filterValue)
      );
    }
    this.totalItems = this.filteredData.length;
    this.updateSummary();
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.updatePaginatedData();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Completed':
        return 'seekClr';
      case 'Booked':
        return 'mer2';
      case 'Cancelled':
        return 'reject';
      default:
        return 'green1';
    }
  }

  viewBookingDetails(row: LotteryBeneficiaryRow): void {
    this.selectedBookingId = row.bookingId;
    this.bookingDetailsView = true;
  }

  onBookingDetailsClosed(): void {
    this.bookingDetailsView = false;
  }

  exportAsXLSX(): void {
    const excelList = this.filteredData.map(row => ({
      BookingReference: row.bookingReferenceNo,
      BookingDate: row.bookingDate,
      MobileNumber: row.mobileNumber,
      FarmerCommunity: row.farmerCommunity,
      FarmerType: row.farmerType,
      ProductName: row.productName,
      CurrentStatus: row.currentStatus
    }));
    this.excelService.exportAsExcelFile(excelList, 'Beneficiary List', this.excelColumns);
  }

  onCancel(): void {
    this.beneficiaryClosed.emit(true);
  }

  goToSubDivision(): void {
    this.beneficiaryClosedToDivision.emit(true);
  }

  goToDivision(): void {
    this.beneficiaryClosed.emit(false);
  }

  get pageHeading(): string {
    if (!this.drillData) {
      return '';
    }
    const schemeComponent = [this.drillData.schemeName, this.drillData.goName, this.drillData.componentName]
      .filter(v => !!v).join(' | ');
    return `${this.drillData.departmentName || ''} | ${this.drillData.financialYearName || ''} | ${schemeComponent}`;
  }

  goToDashboard(): void {
    this.toDashboardComp.emit(true);
  }
}
